import React, { useEffect, useState } from "react";
import Form from "react-bootstrap/Form";
import { LuSearch, LuSearchX } from "react-icons/lu";
import SearchButton from "./SearchButton";
import { Alert } from "react-bootstrap";

import ReactTooltip from "react-tooltip";
import "./search-button.css";

/**
 * SearchSection Component
 * 
 * Search input interface with validation for datatable search queries. Validates search
 * terms against stop words and minimum character length. Provides search history and
 * result status notifications. Clears search to reset table to initial state.
 * 
 * @param {Object} props - Component props
 * @param {Object} props.state - Search state from parent
 * @param {string} props.argument - Current filter/search argument
 * @param {string} props.placeholderText - Search input placeholder text
 * @param {Function} props.changeFilters - Callback for filter changes
 * @param {Function} props.enterSearch - Callback when search input changes
 * @param {Function} props.submitSearch - Callback to submit search query
 * @param {Function} props.resetSearch - Reset search callback
 * @param {boolean} props.isLoading - Loading state indicator
 * @returns {JSX.Element} Search input with validation and alert notifications
 */
const SearchSection = ({ props }) => {
  const {
    state,
    argument,
    placeholderText,
    changeFilters,
    enterSearch,
    submitSearch,
    resetSearch,
    isLoading,
  } = props;

  // hook for closing and opening of alert component
  const [showAlert, setShowAlert] = useState(false);

  // hook for alert message
  const [message, setMessage] = useState("");

  const handleAlertClose = () => {
    setShowAlert(false);
  };

  const handleAlertOpen = () => {
    setShowAlert(true);
  };
  // a list of stop words that used for validation when a search or filter is made
  const stopWords = [
    "a",
    "an",
    "and",
    "are",
    "as",
    "at",
    "be",
    "but",
    "by",
    "for",
    "if",
    "in",
    "into",
    "is",
    "it",
    "no",
    "not",
    "of",
    "on",
    "or",
    "such",
    "that",
    "the",
    "their",
    "then",
    "there",
    "these",
    "they",
    "this",
    "to",
    "was",
    "will",
    "with",
  ];

  const checkIfStopWord = (arr, keyWord) => {
    return arr.some((item) => keyWord.trim() === item);
  };

  const checkIfLessThan4Letters = (keyWord) => {
    return keyWord.length < 4;
  };

  const handleSubmit = (e) => {
    e.preventDefault(); // prevent form submission default behavior

    if (checkIfStopWord(stopWords, argument.searchTerm)) {
      setMessage(
        "Stop words like “a”, “the”, “is” and “are” are not allowed, please enter another keyword!"
      );
      handleAlertOpen();
    } else if (checkIfLessThan4Letters(argument.searchTerm)) {
      setMessage("Please enter more than 3 letters!");
      handleAlertOpen();
    } else {
      submitSearch(e, props);
    }
  };

  const handleSubmitOnKeyDown = (e) => {
    if (e.code === "Enter" || e.code === "NumpadEnter") {
      e.preventDefault(); // Always prevent default on Enter

      if (checkIfStopWord(stopWords, argument.searchTerm)) {
        setMessage(
          "Stop words like “a”, “the”, “is” and “are” are not allowed, please enter another keyword!"
        );
        handleAlertOpen();
      } else if (checkIfLessThan4Letters(argument.searchTerm)) {
        setMessage("Please enter more than 3 letters!");
        handleAlertOpen();
      } else {
        submitSearch(e, props);
      }
    }
  };


  useEffect(() => {
    // reset states when argument state changes
    handleAlertClose();
    setMessage("");
  }, [argument]);
  return (
    <>
      <ReactTooltip
        className="navbartooltip"
        place="bottom"
        type="dark"
        effect="solid"
      />

      <div className="shutdownsearchelements">
        {/* on desktop dimensions */}
        <div className="shutdowndatasearchbox desktop">
          <Form className="search-form" style={{ flexGrow: 1 }}>
            <Form.Group className="inputgroup" controlId="formSearch" style={{ marginBottom: "0px" }}>
              <Form.Control
                className="search-input"
                value={argument.searchTerm}
                type="search"
                name="searchTerm"
                placeholder={placeholderText}
                onChange={changeFilters}
                onKeyDown={enterSearch}
                disabled={isLoading}
              />
              <span className="search-buttons search">
                {/* reset search term button */}
                <span
                  className={
                    isLoading
                      ? "disabled search-button-left"
                      : "search-button-left"
                  }
                  data-tip="Reset search"
                  onClick={!isLoading ? (e) => resetSearch(e) : () => { }}
                >
                  <LuSearchX size={"1rem"} />
                </span>
                {/* search button */}
                <span
                  className={
                    isLoading
                      ? "disabled search-button-right"
                      : "search-button-right"
                  }
                  data-tip="Enter search"
                  onClick={!isLoading ? (e) => handleSubmit(e) : () => { }}
                >
                  <LuSearch size={"1rem"} />
                </span>
              </span>
              <Alert
                show={showAlert}
                variant="danger"
                onClose={handleAlertClose}
                dismissible
                className="search-alert-div"
              >
                {message}
              </Alert>
            </Form.Group>
          </Form>

          {/* <span className="search-button-right search">
            <LuSearch
              value={argument.searchTerm}
              onChange={changeFilters}
              onKeyDown={enterSearch}
              placeholderText={placeholderText}
            />
          </span> */}
        </div>

        {/* on mobile dimensions */}
        <div className="shutdowndatasearchbuttons">
          {!state.searchApplied ? (
            <span className="shutdownfilterbuttons mobile" disabled>
              <LuSearchX
                size={"1.2rem"}
                style={{ color: " rgba(16, 16, 16, 0.3)" }}
              />
            </span>
          ) : (
            <span
              className={
                isLoading
                  ? "disabled shutdownfilterbuttons mobile"
                  : "shutdownfilterbuttons mobile"
              }
              onClick={!isLoading ? (e) => resetSearch(e) : () => { }}
              disabled={isLoading}
            >
              <LuSearchX size={"1.2rem"} />
            </span>
          )}

          <SearchButton
            className="mobile"
            value={argument.searchTerm}
            onChange={changeFilters}
            onKeyDown={handleSubmitOnKeyDown}
            placeholderText={placeholderText}
            message={message}
            handleAlertClose={handleAlertClose}
            showAlert={showAlert}
            isLoading={isLoading}
            handleSubmit={handleSubmit}
          />
        </div>
      </div>
    </>
  );
};

export default SearchSection;
